import { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n/I18nContext";
import { applicableAuToit, libelleGroupe, libelleOption, memeLibelle } from "@/lib/roof";

/**
 * Sélection des produits variables d'UNE propriété (bardeau, membrane, etc.).
 *
 * Le catalogue vient des réglages du compte (`settings.choix`). Ce qui est
 * retenu ici — modèle, couleur, prix — est figé dans la soumission : changer le
 * catalogue plus tard ne modifiera pas les soumissions déjà envoyées.
 *
 * `lang` est la langue de la SOUMISSION, pas celle de l'interface : les
 * libellés enregistrés sont ceux que le client lira sur son devis.
 */
export default function ChoixMateriaux({ settings, valeur = [], onChange, platsPresents = [false], lang }) {
  const { lang: langUI } = useI18n();
  const langue = lang === "en" || lang === "fr" || lang === "es" ? lang : langUI;
  const fr = langue === "fr";
  const es = langue === "es";
  const groupes = settings?.choix || [];

  // Un chantier peut mêler une maison en pente et un cabanon à membrane : le
  // catalogue doit alors proposer les produits des DEUX. Un produit reste offert
  // dès qu'il convient à au moins un des bâtiments présents ; le calcul, lui, ne
  // l'applique qu'à ceux dont le toit correspond.
  const types = platsPresents.length ? platsPresents : [false];
  const options = (g) => (g.options || []).filter((o) => types.some((p) => applicableAuToit(o, p)));
  // Le libellé stocké peut venir de l'autre langue (le devis a changé de langue
  // en cours de route) : on rattache la sélection au groupe, pas à son nom.
  const choixDuGroupe = (g) => (valeur || []).find((c) => memeLibelle(g, "nom", c.groupe)) || {};
  const optionRetenue = (g) => options(g).find((o) => memeLibelle(o, "label", choixDuGroupe(g).produit));

  // Changer le type de toit invalide certains produits : un bardeau doit
  // disparaître d'un devis de toit plat, sinon son prix resterait facturé.
  // Changer la langue du devis doit réécrire les libellés déjà retenus.
  useEffect(() => {
    const suite = [];
    let modifie = false;
    groupes.forEach((g) => {
      const c = choixDuGroupe(g);
      if (!c.produit) return;
      const o = optionRetenue(g);
      if (!o) { modifie = true; return; }         // produit devenu inapplicable
      const nomG = libelleGroupe(g, langue);
      const nomP = libelleOption(o, langue);
      if (c.groupe !== nomG || c.produit !== nomP) modifie = true;
      suite.push({ ...c, groupe: nomG, produit: nomP });
    });
    if (modifie || suite.length !== (valeur || []).filter((c) => c.produit).length) onChange(suite);
    // Volontairement sur les types de toit présents et la langue : ce sont les
    // deux bascules qui rendent une sélection caduque, pas chaque clic.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types.join(","), langue]);

  if (groupes.length === 0) return null;

  const definir = (g, suite) => {
    const nomG = libelleGroupe(g, langue);
    const actuel = choixDuGroupe(g);
    const maj = { groupe: nomG, produit: "", couleur: "", price_per_square: 0, cost_per_square: 0, toit: "tous", ...actuel, ...suite };
    // On reconstruit dans l'ordre du catalogue : le PDF suit cet ordre.
    const ordonne = groupes
      .map((x) => (x === g ? maj : choixDuGroupe(x)))
      .filter((c) => c && c.produit);
    onChange(ordonne);
  };

  return (
    <div className="space-y-4">
      {groupes.map((g, gi) => {
        const choisi = choixDuGroupe(g);
        const dispo = options(g);
        const option = optionRetenue(g);
        // Suggestions dans la langue de la soumission ; l'ancienne liste
        // unilingue sert de repli pour les catalogues pas encore traduits.
        const couleurs = (option?.[`couleurs_${langue}`]?.length
          ? option[`couleurs_${langue}`]
          : option?.couleurs) || [];
        const listeId = `couleurs-${gi}`;
        const nomGroupe = libelleGroupe(g, langue);
        return (
          <div key={gi} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-ink-faint">{nomGroupe}</Label>
              <select
                data-testid={`choix-produit-${gi}`}
                value={choisi.produit || ""}
                onChange={(e) => {
                  const o = dispo.find((x) => libelleOption(x, langue) === e.target.value);
                  // Changer de produit remet la couleur à zéro : les gammes n'ont
                  // pas les mêmes teintes, garder l'ancienne créerait une soumission fausse.
                  definir(g, {
                    produit: o ? libelleOption(o, langue) : "",
                    price_per_square: o ? Number(o.price_per_square) || 0 : 0,
                    cost_per_square: o ? Number(o.cost_per_square) || 0 : 0,
                    // Le type de toit du produit voyage avec la sélection :
                    // sans lui, un bardeau serait facturé sur le cabanon plat.
                    toit: o?.toit || "tous",
                    couleur: "",
                  });
                }}
                className="mt-1 w-full bg-field border border-line-strong rounded-md px-3 py-2 text-sm focus:border-orange-500 outline-none"
              >
                <option value="">{fr ? "— Aucun —" : es ? "— Ninguno —" : "— None —"}</option>
                {dispo.map((o, oi) => (
                  <option key={oi} value={libelleOption(o, langue)}>
                    {libelleOption(o, langue)} · {Number(o.price_per_square) || 0} $/{fr ? "carré" : es ? "cuad." : "sq"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-ink-faint">
                {fr ? "Couleur" : "Color"}{/* « Color » convient à l'anglais comme à l'espagnol */}
              </Label>
              {/* Liste de suggestions, mais saisie libre : les couleurs changent
                  plus vite que le catalogue. */}
              <Input
                data-testid={`choix-couleur-${gi}`}
                list={couleurs.length ? listeId : undefined}
                value={choisi.couleur || ""}
                disabled={!choisi.produit}
                onChange={(e) => definir(g, { couleur: e.target.value })}
                placeholder={choisi.produit
                  ? (fr ? "Ex. Charbon" : es ? "Ej. Carbón" : "e.g. Charcoal")
                  : (fr ? "Choisir un produit d'abord" : es ? "Elija primero un producto" : "Pick a product first")}
                className="mt-1 bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500 disabled:opacity-40"
              />
              {couleurs.length > 0 && (
                <datalist id={listeId}>
                  {couleurs.map((c) => <option key={c} value={c} />)}
                </datalist>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
